import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Grid,
  Container,
  Chip,
  Alert,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  AccessAlarm as AccessAlarmIcon,
  Quiz as QuizIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Send as SendIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import SideBar from "../Component/SideBar";
import { useParams } from "react-router-dom";
import { fetchExamById, submitTestAnswers } from "../Api/examApi";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

export default function QuizPage() {
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [timer, setTimer] = useState(600);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [showQuestionPaper, setShowQuestionPaper] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);
  const timerRef = useRef(null);

  // Timer logic
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          alert("Time is up! Your test will be submitted automatically.");
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, []);

  const formatTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const getTimerColor = () => {
    if (timer <= 60) return "#f44336"; // Red for last minute
    if (timer <= 300) return "#ff9800"; // Orange for last 5 minutes
    return "#4caf50"; // Green for normal time
  };

  // Load exam data
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchExamById(id);
        setExam(data);
        setQuestions(data.questionInformation?.questions || []);
        setTimer(data.generalInformation?.durationInMinutes * 60 || 600);
      } catch (err) {
        console.error("Failed to load questions", err);
        alert("Failed to load test.");
      }
    };
    loadData();
  }, [id]);

  // Handle answer selection
  const handleAnswerChange = (qIndex, selectedAnswer) => {
    if (hasSubmitted) return;

    const question = questions[qIndex];
    if (question.IsMultipleCorrect) {
      setAnswers((prev) => {
        const currentSelections = prev[qIndex] || [];
        const newSelections = currentSelections.includes(selectedAnswer)
          ? currentSelections.filter((id) => id !== selectedAnswer)
          : [...currentSelections, selectedAnswer];
        return { ...prev, [qIndex]: newSelections };
      });
    } else {
      setAnswers((prev) => ({ ...prev, [qIndex]: selectedAnswer }));
    }
  };

  const handleClearAnswer = (qIndex) => {
    if (hasSubmitted) return;
    setAnswers((prev) => {
      const newAnswers = { ...prev };
      delete newAnswers[qIndex];
      return newAnswers;
    });
  };

  // Submit test
  const handleSubmit = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const formattedAnswers = questions.map((question, index) => {
      const selected = answers[index];
      return {
        questionId: question.id,
        selectedOptions: Array.isArray(selected)
          ? selected
          : selected
          ? [selected]
          : [],
      };
    });

    const payload = { id, answers: formattedAnswers };

    try {
      const response = await submitTestAnswers(payload);
      if (response.questionInformation?.questions) {
        setQuestions(response.questionInformation.questions);
      }
      setResultData(response.result);
      setShowResult(true);
      setHasSubmitted(true);
    } catch (error) {
      alert("Failed to submit the test.");
    }
  };

  const getQuestionStatus = (index) => {
    if (index === currentQIndex) return "current";
    if (answers[index] !== undefined) return "answered";
    return "unanswered";
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "current":
        return {
          backgroundColor: "#2196F3",
          color: "#fff",
          "&:hover": { backgroundColor: "#1976D2" },
        };
      case "answered":
        return {
          backgroundColor: "#4CAF50",
          color: "#fff",
          "&:hover": { backgroundColor: "#388E3C" },
        };
      default:
        return {
          backgroundColor: "#9E9E9E",
          color: "#fff",
          "&:hover": { backgroundColor: "#757575" },
        };
    }
  };

  // Result view
  if (showResult && resultData && exam) {
    const getStatusText = () => {
      switch (resultData.isPassed) {
        case 0:
          return { text: "Not Evaluated", color: "#9e9e9e", icon: "⏳" };
        case 1:
          return { text: "Passed", color: "#4caf50", icon: "✅" };
        case 2:
          return { text: "Failed", color: "#f44336", icon: "❌" };
        default:
          return { text: "Unknown", color: "#9e9e9e", icon: "❓" };
      }
    };

    const status = getStatusText();

    return (
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <SideBar />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            px: { xs: 1, sm: 2, md: 3 },
            py: { xs: 2, sm: 3 },
            mt: { xs: 8, sm: 9, md: 10 },
            bgcolor: "#f5f5f5",
            minHeight: "calc(100vh - 64px)",
          }}
        >
          <Container maxWidth="lg">
            <Paper
              elevation={3}
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                borderRadius: 3,
                bgcolor: "#fff",
              }}
            >
              {/* Success Header */}
              <Box textAlign="center" mb={4}>
                <CheckCircleIcon sx={{ fontSize: 60, color: "#4caf50" }} />
                <Typography
                  variant={isSmallScreen ? "h5" : "h4"}
                  color="success.main"
                  gutterBottom
                  fontWeight="bold"
                >
                  Test Submitted Successfully!
                </Typography>
              </Box>

              {/* Test Info Card */}
              <Card sx={{ mb: 1, bgcolor: "#f8f9fa" }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    color="primary"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    Test Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Typography>
                          <strong>Test Name:</strong>{" "}
                          {exam.generalInformation.name}
                        </Typography>
                        <Typography>
                          <strong>Subject:</strong>{" "}
                          {exam.generalInformation.subject}
                        </Typography>
                        <Typography>
                          <strong>Topic:</strong>{" "}
                          {exam.generalInformation.topic}
                        </Typography>
                        <Typography>
                          <strong>Language:</strong>{" "}
                          {exam.generalInformation.language}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Typography>
                          <strong>Duration:</strong>{" "}
                          {exam.generalInformation.durationInMinutes} minutes
                        </Typography>
                        <Typography>
                          <strong>Total Questions:</strong> {resultData.total}
                        </Typography>
                        <Typography>
                          <strong>Attempted:</strong> {resultData.attempted}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Results Card */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    📊 Results Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Paper
                        sx={{ p: 1, textAlign: "center", bgcolor: "#e8f5e9" }}
                      >
                        <Typography
                          variant="h4"
                          color="success.main"
                          fontWeight="bold"
                        >
                          {resultData.correct}
                        </Typography>
                        <Typography variant="body2">Correct</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper
                        sx={{ p: 1, textAlign: "center", bgcolor: "#ffebee" }}
                      >
                        <Typography
                          variant="h4"
                          color="error.main"
                          fontWeight="bold"
                        >
                          {resultData.incorrect}
                        </Typography>
                        <Typography variant="body2">Incorrect</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper
                        sx={{ p: 1, textAlign: "center", bgcolor: "#fff3e0" }}
                      >
                        <Typography
                          variant="h4"
                          color="warning.main"
                          fontWeight="bold"
                        >
                          {resultData.marks}
                        </Typography>
                        <Typography variant="body2">Marks</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper
                        sx={{ p: 1, textAlign: "center", bgcolor: "#e3f2fd" }}
                      >
                        <Typography
                          variant="h4"
                          color="primary.main"
                          fontWeight="bold"
                        >
                          {resultData.percentage ?? 0}%
                        </Typography>
                        <Typography variant="body2">Percentage</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* View Results Toggle Button */}
              <Box textAlign="center">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    showQuestionPaper ? (
                      <VisibilityOffIcon />
                    ) : (
                      <VisibilityIcon />
                    )
                  }
                  onClick={() => setShowQuestionPaper(!showQuestionPaper)}
                  sx={{ minWidth: 200 }}
                >
                  {showQuestionPaper
                    ? "Hide Detailed Results"
                    : "View Detailed Results"}
                </Button>
              </Box>

              {/* Detailed Results - Collapsible */}
              <Collapse in={showQuestionPaper}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      📘 Detailed Answer Sheet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                      Review your answers with correct solutions and
                      explanations
                    </Typography>

                    {questions.map((question, index) => {
                      const selectedOptions = answers[index] || [];
                      const isQuestionCorrect = question.correct === true;

                      return (
                        <Paper
                          key={question.id || index}
                          elevation={1}
                          sx={{
                            p: { xs: 2, sm: 3 },
                            mb: 2,
                            border: `2px solid ${
                              isQuestionCorrect ? "#4caf50" : "#f44336"
                            }`,
                            backgroundColor: isQuestionCorrect
                              ? "#f1f8e9"
                              : "#ffebee",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                              mb: 2,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              component="div"
                              sx={{
                                color: isQuestionCorrect
                                  ? "#2e7d32"
                                  : "#d32f2f",
                                fontWeight: 600,
                                flex: 1,
                              }}
                            >
                              <strong>Q{index + 1}:</strong>{" "}
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: question.question,
                                }}
                              />
                            </Typography>
                            <Chip
                              icon={
                                isQuestionCorrect ? (
                                  <CheckCircleIcon />
                                ) : (
                                  <CancelIcon />
                                )
                              }
                              label={isQuestionCorrect ? "Correct" : "Wrong"}
                              size="small"
                              color={isQuestionCorrect ? "success" : "error"}
                              sx={{ minWidth: 80 }}
                            />
                          </Box>

                          <Box sx={{ pl: { xs: 0, sm: 2 } }}>
                            {question.options.map((opt) => {
                              const isSelected = question.IsMultipleCorrect
                                ? selectedOptions.includes(opt.id)
                                : selectedOptions === opt.id;
                              const isOptionCorrect = opt.isCorrect === true;

                              let chipProps = {
                                variant: "outlined",
                                size: "small",
                              };

                              if (isOptionCorrect && isSelected) {
                                chipProps = {
                                  ...chipProps,
                                  color: "success",
                                  label: "✓ Correct & Selected",
                                };
                              } else if (isOptionCorrect) {
                                chipProps = {
                                  ...chipProps,
                                  color: "success",
                                  label: "✓ Correct Answer",
                                };
                              } else if (isSelected) {
                                chipProps = {
                                  ...chipProps,
                                  color: "error",
                                  label: "✗ Your Answer",
                                };
                              }

                              return (
                                <Box
                                  key={opt.id}
                                  sx={{
                                    mb: 1,
                                    p: 2,
                                    borderRadius: 1,
                                    backgroundColor: isOptionCorrect
                                      ? isSelected
                                        ? "#e8f5e9"
                                        : "#f1f8e9"
                                      : isSelected
                                      ? "#ffebee"
                                      : "#fafafa",
                                    border: `1px solid ${
                                      isOptionCorrect
                                        ? "#4caf50"
                                        : isSelected
                                        ? "#f44336"
                                        : "#e0e0e0"
                                    }`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      flex: 1,
                                    }}
                                  >
                                    {question.IsMultipleCorrect ? (
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        disabled
                                        style={{ marginRight: 8 }}
                                      />
                                    ) : (
                                      <input
                                        type="radio"
                                        checked={isSelected}
                                        disabled
                                        style={{ marginRight: 8 }}
                                      />
                                    )}
                                    <Typography
                                      sx={{
                                        fontWeight:
                                          isSelected || isOptionCorrect
                                            ? 600
                                            : 400,
                                        color: isOptionCorrect
                                          ? "#2e7d32"
                                          : isSelected
                                          ? "#d32f2f"
                                          : "#333",
                                      }}
                                    >
                                      <strong>{opt.id}.</strong> {opt.answer}
                                    </Typography>
                                  </Box>
                                  {(isSelected || isOptionCorrect) && (
                                    <Chip {...chipProps} />
                                  )}
                                </Box>
                              );
                            })}
                          </Box>

                          {/* Explanation */}
                          {question.explanation && (
                            <Box
                              sx={{
                                mt: 1,
                                pt: 1,
                                borderTop: "1px solid #e0e0e0",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  mb: 1,
                                  color: "#1976d2",
                                }}
                              >
                                💡 Explanation:
                              </Typography>
                              <Box
                                sx={{
                                  p: 1,
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: 1,
                                  border: "1px solid #dee2e6",
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: question.explanation,
                                }}
                              />
                            </Box>
                          )}
                        </Paper>
                      );
                    })}
                  </CardContent>
                </Card>
              </Collapse>
            </Paper>
          </Container>
        </Box>
      </Box>
    );
  }

  // Main Quiz View
  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <SideBar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "#f5f5f5",
          minHeight: "100vh",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            px: { xs: 1, sm: 2, md: 3 },
            py: { xs: 2, sm: 3 },
            mt: { xs: 8, sm: 9, md: 10 },
          }}
        >
          {/* Header */}
          <Paper
            elevation={2}
            sx={{ p: { xs: 2, sm: 3 }, mb: 2, borderRadius: 2 }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant={isSmallScreen ? "subtitle1" : "h6"}
                    component="div"
                  >
                    <Box
                      component="span"
                      fontWeight="bold"
                      color="primary.main"
                    >
                      Test :
                    </Box>{" "}
                    {exam?.generalInformation?.name || "N/A"}
                  </Typography>
                  <Typography
                    variant={isSmallScreen ? "subtitle1" : "h6"}
                    component="div"
                  >
                    <Box
                      component="span"
                      fontWeight="bold"
                      color="primary.main"
                    >
                      Subject :
                    </Box>{" "}
                    {exam?.generalInformation?.subject || "N/A"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    bgcolor: getTimerColor(),
                    color: "white",
                    borderRadius: 2,
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    fontWeight: 600,
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                  }}
                >
                  <AccessAlarmIcon />
                  Time Left : {formatTime(timer)}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {hasSubmitted ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              You have already submitted this test. You cannot go back or change
              answers.
            </Alert>
          ) : questions.length === 0 ? (
            <Alert severity="info">No questions available.</Alert>
          ) : (
            <Grid container spacing={2}>
              {/* Question Section */}
              <Grid item xs={12} lg={8}>
                <Paper sx={{ p: { xs: 1, sm: 2 }, borderRadius: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    {questions[currentQIndex].IsMultipleCorrect && (
                      <Chip
                        label="Multiple Correct"
                        color="warning"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  <Box
                    display="flex"
                    gap={1}
                    mb={1}
                    sx={{
                      lineHeight: 1,
                      fontSize: { xs: "1rem", sm: "1.1rem" },
                    }}
                  >
                    <Typography variant="body1">
                      Question {currentQIndex + 1} :
                    </Typography>
                    <Typography
                      variant="body1"
                      component="div"
                      dangerouslySetInnerHTML={{
                        __html: questions[currentQIndex].question,
                      }}
                    />
                  </Box>

                  <FormControl component="fieldset" sx={{ width: "100%" }}>
                    {questions[currentQIndex].IsMultipleCorrect ? (
                      <Stack spacing={1}>
                        {questions[currentQIndex].options.map((option) => (
                          <Paper
                            key={option.id}
                            elevation={0}
                            sx={{
                              p: 1,
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                                borderColor: "#2196F3",
                              },
                              backgroundColor: (
                                answers[currentQIndex] || []
                              ).includes(option.id)
                                ? "#e3f2fd"
                                : "transparent",
                              borderColor: (
                                answers[currentQIndex] || []
                              ).includes(option.id)
                                ? "#2196F3"
                                : "#e0e0e0",
                            }}
                            onClick={() =>
                              !hasSubmitted &&
                              handleAnswerChange(currentQIndex, option.id)
                            }
                          >
                            <FormControlLabel
                              control={
                                <input
                                  type="checkbox"
                                  checked={(
                                    answers[currentQIndex] || []
                                  ).includes(option.id)}
                                  onChange={() =>
                                    handleAnswerChange(currentQIndex, option.id)
                                  }
                                  disabled={hasSubmitted}
                                  style={{ pointerEvents: "none" }}
                                />
                              }
                              label={`${option.id}. ${option.answer}`}
                              sx={{
                                margin: 0,
                                pointerEvents: "none",
                                "& .MuiFormControlLabel-label": {
                                  fontSize: { xs: "0.9rem", sm: "1rem" },
                                },
                              }}
                            />
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <RadioGroup
                        value={answers[currentQIndex] || ""}
                        onChange={(e) =>
                          handleAnswerChange(currentQIndex, e.target.value)
                        }
                      >
                        <Stack spacing={1}>
                          {questions[currentQIndex].options.map((option) => (
                            <Paper
                              key={option.id}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                cursor: "pointer",
                                "&:hover": {
                                  backgroundColor: "#f5f5f5",
                                  borderColor: "#2196F3",
                                },
                                backgroundColor:
                                  answers[currentQIndex] === option.id
                                    ? "#e3f2fd"
                                    : "transparent",
                                borderColor:
                                  answers[currentQIndex] === option.id
                                    ? "#2196F3"
                                    : "#e0e0e0",
                              }}
                              onClick={() =>
                                !hasSubmitted &&
                                handleAnswerChange(currentQIndex, option.id)
                              }
                            >
                              <FormControlLabel
                                value={option.id}
                                control={
                                  <Radio
                                    disabled={hasSubmitted}
                                    sx={{ pointerEvents: "none" }}
                                  />
                                }
                                label={`${option.id}. ${option.answer}`}
                                sx={{
                                  margin: 0,
                                  pointerEvents: "none",
                                  "& .MuiFormControlLabel-label": {
                                    fontSize: { xs: "0.9rem", sm: "1rem" },
                                  },
                                }}
                              />
                            </Paper>
                          ))}
                        </Stack>
                      </RadioGroup>
                    )}
                  </FormControl>

                  {/* Navigation Buttons */}
                  <Box
                    sx={{
                      mt: 1,
                      pt: 1,
                      borderTop: "1px solid #e0e0e0",
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        startIcon={<NavigateBeforeIcon />}
                        disabled={currentQIndex === 0 || hasSubmitted}
                        onClick={() => setCurrentQIndex(currentQIndex - 1)}
                        size={isSmallScreen ? "small" : "medium"}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<ClearIcon />}
                        disabled={hasSubmitted}
                        onClick={() => handleClearAnswer(currentQIndex)}
                        size={isSmallScreen ? "small" : "medium"}
                      >
                        Clear
                      </Button>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      {currentQIndex === questions.length - 1 ? (
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<SendIcon />}
                          disabled={hasSubmitted}
                          onClick={() =>
                            window.confirm(
                              "Are you sure you want to submit the test?"
                            ) && handleSubmit()
                          }
                          size={isSmallScreen ? "small" : "medium"}
                          sx={{ fontWeight: "bold" }}
                        >
                          Submit Test
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          endIcon={<NavigateNextIcon />}
                          disabled={hasSubmitted}
                          onClick={() => setCurrentQIndex(currentQIndex + 1)}
                          size={isSmallScreen ? "small" : "medium"}
                        >
                          Save & Next
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Navigation Panel */}
              <Grid item xs={12} lg={4}>
                <Paper
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    position: { lg: "sticky" },
                    top: { lg: 100 },
                  }}
                >
                  {/* Mobile Toggle for Navigation */}
                  {isMobile && (
                    <Box sx={{ mb: 2 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => setShowNavigation(!showNavigation)}
                        endIcon={
                          showNavigation ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )
                        }
                      >
                        Question Navigation ({Object.keys(answers).length}/
                        {questions.length} answered)
                      </Button>
                    </Box>
                  )}

                  <Collapse in={!isMobile || showNavigation}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      textAlign="center"
                      color="primary.main"
                    >
                      Question Navigation
                    </Typography>

                    {/* Legend */}
                    <Box sx={{ mb: 2 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        justifyContent="center"
                      >
                        <Chip
                          label="Current"
                          size="small"
                          sx={{ bgcolor: "#2196F3", color: "white" }}
                        />
                        <Chip
                          label="Answered"
                          size="small"
                          sx={{ bgcolor: "#4CAF50", color: "white" }}
                        />
                        <Chip
                          label="Not Answered"
                          size="small"
                          sx={{ bgcolor: "#9E9E9E", color: "white" }}
                        />
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(45px, 1fr))",
                        gap: 1,
                        mb: 3,
                      }}
                    >
                      {questions.map((_, index) => {
                        const status = getQuestionStatus(index);
                        return (
                          <Button
                            key={index}
                            variant="contained"
                            onClick={() =>
                              !hasSubmitted && setCurrentQIndex(index)
                            }
                            sx={{
                              minWidth: 45,
                              minHeight: 45,
                              fontSize: "0.875rem",
                              fontWeight: "bold",
                              ...getStatusStyles(status),
                              transition: "all 0.2s ease-in-out",
                              "&:disabled": {
                                opacity: 0.6,
                              },
                            }}
                            disabled={hasSubmitted}
                          >
                            {index + 1}
                          </Button>
                        );
                      })}
                    </Box>

                    {/* Progress Info */}
                    <Box
                      sx={{ mb: 3, p: 2, bgcolor: "#f8f9fa", borderRadius: 1 }}
                    >
                      <Typography
                        variant="body2"
                        textAlign="center"
                        color="text.secondary"
                      >
                        Progress: {Object.keys(answers).length} /{" "}
                        {questions.length} questions answered
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          bgcolor: "#e0e0e0",
                          borderRadius: 1,
                          height: 8,
                        }}
                      >
                        <Box
                          sx={{
                            width: `${
                              (Object.keys(answers).length / questions.length) *
                              100
                            }%`,
                            bgcolor: "#4caf50",
                            height: "100%",
                            borderRadius: 1,
                            transition: "width 0.3s ease",
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Submit Button */}
                    <Button
                      variant="contained"
                      color="error"
                      fullWidth
                      startIcon={<SendIcon />}
                      disabled={hasSubmitted}
                      onClick={() =>
                        window.confirm(
                          "Are you sure you want to submit the test?"
                        ) && handleSubmit()
                      }
                      sx={{
                        py: 1.5,
                        fontWeight: "bold",
                        fontSize: "1rem",
                      }}
                    >
                      Submit Test
                    </Button>
                  </Collapse>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>
    </Box>
  );
}
